<template>
  <v-dialog
    width="800"
    v-model="opened"
    :fullscreen="$vuetify.breakpoint.xsOnly"
    @keydown.enter="addComment()"
    persistent
    @keydown.esc="$emit('update:opened', false)"
  >
    <v-card flat v-if="selectedQuestion">
      <v-card-title class="title">
        <v-layout align-start>
          {{ $t('questionnaireNote.notes') }}
          <v-spacer />
          <v-icon
            style="cursor: pointer"
            class="error--text ml-2"
            size="18"
            @click="$emit('update:opened', false)"
          >
            clear
          </v-icon>
        </v-layout>
      </v-card-title>
      <v-alert
        v-if="currentUser.is_advisor"
        :value="true"
        outline
        block
        :icon="$vuetify.breakpoint.smAndUp ? 'info' : ''"
        color="primary"
        class="text-xs-left mx-2 font-weight-bold"
      >
        Your notes will not be visible to the client.
      </v-alert>
      <v-alert
        v-else-if="!currentUser.is_advisor && editable"
        :value="true"
        outline
        block
        :icon="$vuetify.breakpoint.smAndUp ? 'info' : ''"
        :color="reasonForComment ? 'primary' : 'error'"
        class="text-xs-left mx-2 font-weight-bold"
      >
        <span v-if="reasonForComment">{{ reasonForComment }}</span>
        <span v-else>
          <span>{{ $t('questionnaireNote.notesInstruction') }} </span>
          <span v-if="currentFirm && !currentFirm.communication_disabled"
            ><a
              style="text-decoration: underline; color: black"
              @click="$emit('open-conversation')"
              >{{ $t('questionnaireNote.clickHereToSendAMessage') }}</a
            ></span
          >
          <span> {{ $t('questionnaireNote.doNotLeaveQuestions') }}</span>
        </span>
      </v-alert>
      <v-alert
        v-else
        :value="true"
        outline
        block
        :icon="$vuetify.breakpoint.smAndUp ? 'info' : ''"
        color="primary"
        class="text-xs-left mx-2"
      >
        {{ $t('questionnaireNote.noteAfterSubmission') }}
        <span v-if="currentFirm && currentFirm.communication_disabled"
          >{{ $t('questionnaireNote.pleaseContactUs') }}
        </span>
        <span v-else
          >Please
          <a
            style="text-decoration: underline; color: black"
            @click="$emit('open-conversation')"
            >{{ $t('questionnaireNote.sendUsAMessage') }}</a
          >
        </span>
        {{ $t('questionnaireNote.additionalInformation') }}
      </v-alert>
      <v-card-text>
        <v-layout column fill-height>
          <v-timeline class="mb-3" dense v-if="!isEmpty(filteredComments)">
            <v-timeline-item
              small
              right
              v-for="(comment, i) in filteredComments"
              :key="i"
              class="mb-2"
            >
              <v-layout align-start column>
                <div class="grey--text text--darken-2 mr-3 text-xs-left">
                  {{ moment(comment.created_at).format('lll') }}
                </div>
                <v-layout align-center class="text-xs-left">
                  <v-chip v-if="comment.from_firm_user" small>Staff</v-chip>
                  <span class="font-weight-bold">{{
                    comment.text.split(': ')[0]
                  }}</span
                  >:
                  <span
                    :style="
                      comment.removed ? 'text-decoration: line-through' : ''
                    "
                    >{{
                      comment.text.replace(
                        comment.text.split(': ')[0] + ': ',
                        '',
                      )
                    }}</span
                  >
                  <v-chip small v-if="comment.removed"
                    >Deleted
                    <v-icon
                      size="20"
                      class="ml-1"
                      @click="toggleCommentRemoved(comment)"
                      >clear</v-icon
                    >
                  </v-chip>
                  <v-tooltip bottom class="ml-2" v-else>
                    <v-icon
                      slot="activator"
                      class="error--text"
                      size="20"
                      @click="toggleCommentRemoved(comment)"
                      >delete</v-icon
                    >
                    <span>Delete</span>
                  </v-tooltip>
                </v-layout>
              </v-layout>
            </v-timeline-item>
          </v-timeline>
          <div v-else class="my-3" />
          <v-layout
            :align-center="$vuetify.breakpoint.mdAndUp"
            :column="$vuetify.breakpoint.smAndDown"
            v-if="
              (!currentUser.is_advisor && editable) || currentUser.is_advisor
            "
          >
            <v-text-field
              class="default-input"
              solo
              hide-details
              :placeholder="$t('questionnaireNote.enterNoteHere')"
              v-model="newComment"
            />
            <v-flex shrink>
              <v-btn
                class="default-input primary text-capitalize"
                @click="addComment()"
                :disabled="!newComment"
                :block="$vuetify.breakpoint.smAndDown"
              >
                {{ $t('questionnaireNote.addNote') }}
              </v-btn>
            </v-flex>
          </v-layout>
        </v-layout>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import API from '@/api';
import moment from 'moment';
import isEmpty from 'lodash/isEmpty';
import { mapGetters } from 'vuex';

export default {
  props: {
    opened: {
      type: Boolean,
      default: false,
    },

    reasonForComment: {
      type: String,
      default: '',
    },

    editable: {
      type: Boolean,
      default: false,
    },

    selectedQuestion: {
      type: Object,
      default: null,
    },

    currentCompanyId: {
      type: Number,
      default: null,
    },
  },

  data() {
    return {
      comments: [],
      newComment: '',
    };
  },

  methods: {
    isEmpty,
    moment,

    async addComment(event) {
      event && event.preventDefault && event.preventDefault();

      try {
        const response = await API.questionnaire.addQuestionnaireComment({
          text: this.newComment,
          questionnaireQuestionId: this.selectedQuestion.id,
          companyId: this.currentCompanyId,
        });

        this.comments.push(response.data);
        this.newComment = '';
      } catch (error) {
        console.error(error);
        this.$store.dispatch('application/setErrorNotification', {
          message:
            'There was a problem adding a comment. Please refresh the page and try again.',
        });
      }
    },
    async toggleCommentRemoved(comment) {
      this.toggleComment(comment);

      try {
        await API.questionnaire.toggleQuestionnaireCommentRemoved({
          commentId: comment.id,
          questionnaireQuestionId: this.selectedQuestion.id,
          companyId: this.currentCompanyId,
        });
      } catch (error) {
        console.error(error);

        this.toggleComment(comment);
        this.$store.dispatch('application/setErrorNotification', {
          message:
            'There was a problem removing the comment. Please refresh the page and try again.',
        });
      }
    },
    toggleComment(comment) {
      this.comments = this.selectedQuestion.filtered_questionnaire_comments.map(
        c => {
          if (c.id === comment.id) c.removed = !c.removed;

          return c;
        },
      );
    },
  },

  computed: {
    ...mapGetters({
      currentUser: 'user',
      currentFirm: 'currentFirm',
    }),
    filteredComments() {
      if (this.currentUser.is_advisor) {
        return this.comments;
      } else {
        return this.comments.filter(c => !c.from_firm_user);
      }
    },
  },
  watch: {
    selectedQuestion(newValue) {
      if (!newValue) return;

      const newComments = newValue.filtered_questionnaire_comments.sort(
        (a, b) => (a.created_at > b.created_at ? 1 : -1),
      );

      this.comments = newComments;
    },
  },
};
</script>
